import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FaUser, FaLock, FaSignInAlt, FaBuilding } from "react-icons/fa";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { loginApi, prefetchEncryptionKey } from "../services/loginService";
import {
  setToken,
  setRefreshToken,
  startTokenTimer,
  markLoggedIn,
} from "../../../Services/tokenUtils";
import { APP_VERSION } from "../../../version";

export default function Login() {
  const navigate = useNavigate();

  // Prefetch RSA key when login page loads — saves ~400ms on login click
  useEffect(() => {
    prefetchEncryptionKey();
    // Release B: surface why the user landed here (forced logout / session ended).
    try {
      const reason = localStorage.getItem("logoutReason");
      if (reason) {
        localStorage.removeItem("logoutReason");
        toast(reason, { icon: "🔒" });
      }
    } catch { /* ignore */ }
  }, []);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shake, setShake] = useState(false);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Frontend Validation
    if (!username.trim() && !password.trim()) {
      triggerShake();
      return toast.error("Username and password are required");
    }
    if (!username.trim()) {
      triggerShake();
      return toast.error("Please enter username");
    }
    if (!password.trim()) {
      triggerShake();
      return toast.error("Please enter password");
    }

    setIsLoading(true);

    try {
      const res = await loginApi(username, password);

      if (res.errCode === 0) {
        // Re-open the token writes that clearToken() gated during the
        // previous logout. Must run BEFORE setToken calls or they silently no-op.
        markLoggedIn();

        // The server resolves which company (and its main store) to preselect —
        // explicit primary, else the user's first assigned company. Folded into
        // loggedUser so pages read one object rather than a second storage key.
        const user = {
          ...res.data.user,
          primaryCompanyId: res.data.primaryCompanyId ?? null,
          primaryStoreId: res.data.primaryStoreId ?? null,
          primaryMainStoreId: res.data.primaryMainStoreId ?? null,
          // Release A: drives the forced change-password redirect (read by ProtectedRoute).
          mustChangePassword: !!res.data.mustChangePassword,
        };
        localStorage.setItem("loggedUser", JSON.stringify(user));
        setToken(res.data.token);
        setRefreshToken(res.data.refreshToken);

        // Permissions now come in the login response (no separate API call needed)
        if (res.data.permissions) {
          localStorage.setItem("userPermissions", JSON.stringify(res.data.permissions));
        }
        // Company-wise roles: per-company permission map { [companyId]: flags }
        try {
          localStorage.setItem("companyPermissions", JSON.stringify(res.data.companyPermissions || {}));
        } catch { /* ignore storage failure */ }
        // Company-wise pricing: { [companyId]: { purchaseFactor, sellingFactor, pricesAllowed, allowStockInPrice } }
        try {
          localStorage.setItem("companyPricing", JSON.stringify(res.data.companyPricing || {}));
        } catch { /* ignore storage failure */ }

        startTokenTimer();
        if (user.mustChangePassword) {
          toast("Please set a new password to continue", { icon: "🔒" });
          navigate("/change-password");
        } else {
          toast.success(`Welcome, ${user.fullName}!`);
          navigate("/dashboard");
        }
      } else if (res.errCode === -426) {
        // Client version below the server's minimum. A page refresh fetches the
        // latest bundle from the server (hashed filename, so it's never stale).
        triggerShake();
        toast.error(res.message || "A newer version is required. Please refresh to update.");
      } else if (res.errCode === -409) {
        // Session limit / already signed in on another system (Release B).
        triggerShake();
        toast.error(res.message || "You are already signed in on another system.");
      } else {
        triggerShake();
        toast.error(res.message || res.errMsg || "Invalid login");
      }
    } catch (error) {
      console.error("Login Error:", error);
      triggerShake();
      toast.error(error.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page min-h-[100dvh] flex items-center justify-center bg-gray-100 p-4">
      <div className={`w-full max-w-md bg-white border rounded-xl shadow-lg p-10 ${shake ? "animate-shake" : ""}`}
           style={shake ? { animation: "shake 0.5s ease-in-out" } : {}}>
        {/* Logo Icon */}
        <div className="flex justify-center mb-8">
          <FaBuilding size={52} className="text-[#1976d2]" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center text-gray-800">
          Stock Management
        </h1>
        <p className="text-center text-sm text-gray-500 mt-7 mb-8">
          Please sign in to continue
        </p>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          {/* Username */}
          <div>
            <label className="flex items-center gap-3 text-gray-700 mb-3 font-medium text-sm">
              <FaUser className="text-gray-500 text-md" />
              Username
            </label>

            <div className="flex items-center border border-gray-300 rounded-md overflow-hidden px-3 focus-within:border-2 focus-within:border-[#1976d2] transition">
              <input
                type="text"
                name="username"
                autoComplete="username"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-3 outline-none text-sm"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="flex items-center gap-3 text-gray-700 mb-3 font-medium text-sm">
              <FaLock className="text-gray-500 text-md" />
              Password
            </label>

            <div className="flex items-center border border-gray-300 rounded-md overflow-hidden px-3 focus-within:border-2 focus-within:border-[#1976d2] transition">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 outline-none text-sm"
                disabled={isLoading}
              />

              {/* Eye Toggle */}
              <span
                className="cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </span>
            </div>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full text-white font-semibold py-3 rounded-md shadow transition flex items-center justify-center gap-2 ${
              isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#1976d2] hover:bg-[#135eb2] active:bg-[#135eb2]"
            }`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Signing in...
              </>
            ) : (
              <>
                <FaSignInAlt className="text-white text-md" />
                Login
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 flex justify-between text-gray-500 text-xs">
          <p>© 2024 Stock Management System</p>
          <p>Version {APP_VERSION}</p>
        </div>
      </div>
    </div>
  );
}
